// ============================================================
// OVERLAP - saying you are inside something.
//
// Normally you cannot be: a move follows the walkable route and stops
// where the route stops. But terrain can be switched off, and then a
// marker can be put anywhere at all - and the rule is that it STAYS
// there when terrain comes back. It is warned, not ejected, and walks
// out normally on its next move (see `reachableFrom`'s escape rule).
//
// That round trip is only honest if the warning actually appears, which
// is what this is for.
//
// Pure. Zero React dependency.
// ============================================================
import type { Solid, Vec3 } from './types';
import { solidGeometry } from './solids';
import { containsPoint } from './lineOfSight';

export interface Overlap {
  /** Solid volumes the marker is inside. The ones that should not happen. */
  blocking: string[];
  /** Painted ground it is standing on: hazard, rough going. Information. */
  ground: string[];
}

const NOTHING: Overlap = { blocking: [], ground: [] };

/**
 * What this point is inside.
 *
 * Tested a hair above the marker's feet, the same probe height
 * `terrainAt` uses, so a painted zone with no real thickness still
 * registers. Permeable stuff is deliberately NOT a warning: walking into
 * smoke or a forest is the whole point of it being permeable.
 */
export function overlapAt(at: Vec3, solids: Solid[], includeHidden = true): Overlap {
  const probe: Vec3 = { x: at.x, y: at.y + 0.01, z: at.z };
  const found: Overlap = { blocking: [], ground: [] };
  for (const solid of solids) {
    if ((includeHidden || !solid.hidden) && containsPoint(solidGeometry(solid), probe)) file(found, solid);
  }
  return found.blocking.length + found.ground.length > 0 ? found : NOTHING;
}

/**
 * Sort one solid the point is inside into what it means.
 *
 * A zone is ground you are standing ON, not a volume you are stuck in -
 * and it is worth knowing about when it is on fire. Each label once.
 */
function file(found: Overlap, solid: Solid): void {
  const label = solid.label || 'terrain';
  if (!isGround(solid)) addLabel(found.blocking, label);
  else if (solid.terrain !== 'normal') addLabel(found.ground, label);
}

function addLabel(list: string[], label: string): void {
  if (!list.includes(label)) list.push(label);
}

/** Permeable, or too thin to be anything but paint. */
function isGround(solid: Solid): boolean {
  return !!solid.permeability || solid.height <= 0.1;
}

/** Is this marker somewhere it could never have walked to? */
export function isStuck(overlap: Overlap): boolean {
  return overlap.blocking.length > 0;
}

/** One line for a readout: what it is inside, worst first. */
export function overlapLabel(overlap: Overlap): string {
  if (overlap.blocking.length) return `inside ${overlap.blocking.join(', ')}`;
  if (overlap.ground.length) return `standing in ${overlap.ground.join(', ')}`;
  return '';
}
